import { useMemo } from 'react'
import { useWeb3React } from 'lib/wagmi-compat/useWeb3React'
import { useLifetimeTokens } from 'state/global/hooks'

/**
 * Find a wallet's session token whatever case it was stored under.
 *
 * The map is written with the address exactly as the wallet reports it, which
 * is checksummed — `0xAAF0938E…`. A lookup that lowercased the address first
 * therefore found nothing, and the reported symptom was the whole point of
 * this hook failing silently: somebody connected, verified, with the blue tick
 * in the header, reading "Sign in with your wallet to start or join one".
 *
 * Reading tolerantly rather than normalising on write, because the tokens are
 * persisted: changing the key would sign out everybody who already has one to
 * fix a bug that only ever affected reading them.
 */
function findToken(
  tokens: Record<string, string> | undefined,
  account: string | null | undefined
): string | undefined {
  if (!tokens || !account) return undefined
  const exact = tokens[account]
  if (exact) return exact
  const wanted = account.toLowerCase()
  const hit = Object.entries(tokens).find(([key]) => key.toLowerCase() === wanted)
  return hit?.[1]
}

/**
 * The signed-in wallet and its lifetime session token, or nothing.
 *
 * Every authenticated community call needs both, and every one of them has to
 * skip cleanly while the wallet is still arriving — the account and the
 * provider do not turn up in the same tick, and treating that gap as "signed
 * out" is what produced a run of unexplained toasts on the licences and
 * rewards pages.
 */
export function useSession(): { account?: string; accessToken?: string; ready: boolean } {
  const { account } = useWeb3React()
  const lifetimeTokens = useLifetimeTokens()
  const accessToken = useMemo(
    () => findToken(lifetimeTokens, account),
    [lifetimeTokens, account]
  )
  return {
    account: account ?? undefined,
    accessToken,
    ready: Boolean(account && accessToken)
  }
}
